import { useEffect, useState } from 'react';
import { Option, SortModel, SubTableColumn } from 'src/components/commons/subTable/types';
import PageInfo from 'src/modules/settings/modules/subTables/types';
import capitalizeFirstWord from 'src/utils/functions/capitalizeFirstWord';
import { AllFilters2 } from 'src/graphql/schema-types';
import { SalesTeamModel } from 'src/models';
import useSalesTeamsStore from 'src/hooks/stores/useSalesTeamsStore';
// import handleError from 'src/utils/functions/handleError';
import COLUMNS, { COLUMNS_MEMBERS } from '../constants';

const useSalesTeamsSettingsTable = () => {
  const [deleting, setDeleting] = useState(false);
  const {
    loading,
    reset,
    dataSource,
    loadPage: onListRender,
    fetchAll,
    deleteOne,
    updateOne,
    createOne,
    setDataSource,
  } = useSalesTeamsStore();

  const [pagination, setPagination] = useState<PageInfo>({ page: 1, pageSize: 25 });
  const [columns, setColumns] = useState<SubTableColumn[]>([]);
  const [columnsMembers, setColumnsMembers] = useState<SubTableColumn[]>([]);
  const [selectedRecord, setSelectedRecord] = useState<any>();
  const [newRecord, setNewRecord] = useState({
    name: '',
  });

  useEffect(() => {
    setColumns(COLUMNS);
    setColumnsMembers(COLUMNS_MEMBERS);
  }, []);

  const autocompleteFieldSearch = (
    searchField: string,
    value: string,
    dataType: string | number | string[],
  ) =>
    fetchAll({
      variables: {
        criteria: {
          pagination: {
            from: 0,
            size: 25,
          },
          filter: [
            {
              fieldName: searchField,
              value,
              dataType,
              operator: AllFilters2.Contains,
            },
          ],
        },
        searchFields: {
          ids: null,
          names: null,
          enabled: null,
        },
      },
    }).then((res) => {
      if (res.data) {
        return {
          results: res?.data?.GetAllDivisions as any[],
          filter: res?.data?.GetAllDivisions?.map((result: any) => ({
            value: result[searchField],
            label: result[searchField],
          })) as Option[],
        };
      }

      return {
        results: [],
        filter: [],
      };
    });

  const handleUpdateRows = (rows: SalesTeamModel[]) => {
    rows.forEach((row) => {
      const { id, name, companySplit } = row;
      updateOne({
        variables: {
          updateSalesTeamInput: {
            id,
            name: capitalizeFirstWord(name!),
            companySplit: Number(companySplit),
          },
        },
      });
    });
  };

  const postData = (args: Record<string, unknown>) => {
    setNewRecord((prev) => ({
      ...prev,
      [args?.dataIndex as string]: args?.value as string,
      autoSave: !!args?.autoSave,
    }));
  };

  const resetNewRecord = () => {
    setNewRecord({
      name: '',
    });
  };

  const createNewRecord = async (record: SalesTeamModel) => {
    const { name, companySplit } = record;
    if (!record || name === '') return;
    try {
      await createOne({
        variables: {
          createSalesTeamInput: {
            name: capitalizeFirstWord(name as string),
            companySplit: Number(companySplit) / 100,
          },
        },
      }).then((res) => {
        setDataSource((prev) => [
          {
            name: res?.data?.CreateSalesTeam?.name,
            companySplit: res?.data?.CreateSalesTeam?.companySplit,
          },
          ...prev,
        ]);
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteRows = async (rows: SalesTeamModel[]) => {
    // const messages = {
    //   400: {
    //     title: 'This record cannot be deleted',
    //     message: 'This value is linked to the company. You can not delete it.',
    //   },
    // };
    setDeleting(true);
    await Promise.allSettled(rows.map((row) => deleteOne(row.id)));
    setDeleting(false);
  };

  const handleClearFilter = () => {
    reset();
  };

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPagination({ page, pageSize });
  };

  const handleFilterChange = (newFilters: Record<string, string[]>) => {
    setPagination({ ...pagination, filters: newFilters, page: 1 });
  };

  const handleSortModelChange = (sortModel: SortModel | undefined) => {
    setPagination({ ...pagination, sort: sortModel });
  };

  return {
    autocompleteFieldSearch,
    handlePaginationChange,
    handleFilterChange,
    handleSortModelChange,
    pagination,
    columns,
    setColumns,
    handleUpdateRows,
    newRecord,
    postData,
    createNewRecord,
    resetNewRecord,
    spinning: loading || deleting,
    dataSource,
    onListRender,
    handleClearFilter,
    handleDeleteRows,
    columnsMembers,
    setSelectedRecord,
    selectedRecord,
  };
};

export default useSalesTeamsSettingsTable;
